/**
 * Moldura de uma seção de cadastro, com os três estados da consulta.
 *
 * Papel: desenha o cabeçalho da seção, o botão de acrescentar e os três
 * estados que a consulta produz — carregando, falhou e vazio —, deixando para
 * `children` apenas a tabela do caso em que há linhas.
 *
 * Como usar: quem monta a seção passa `vazia` já resolvido, e não a lista: são
 * as duas consultas da página que sabem se o array chegou vazio ou se ainda não
 * chegou. `children` só é desenhado quando há o que mostrar, de modo que a
 * tabela de dentro não precisa tratar lista ausente.
 *
 * O botão de acrescentar some inteiro quando `onNovo` não vem, que é o caso de
 * quem tem nível `ver` e o da seção que só mostra o que outra tela cadastra.
 * Desabilitá-lo em vez de escondê-lo ofereceria uma ação que a API recusaria com
 * 403 depois do clique. Quando ele existe, aparece duas vezes — uma vez ao lado
 * do título, sempre, e de novo dentro do `Vazio` quando não há nenhuma linha —,
 * e é o mesmo `onNovo` nos dois lugares.
 *
 * `descricao` e o botão de acrescentar podem vir preenchidos ao mesmo tempo:
 * quando os dois chegam, o `CabecalhoDeSecao` desce a frase para baixo do
 * título e deixa o botão sozinho à direita; sem o botão — para quem só tem
 * `ver`, inclusive —, a frase volta para a direita, na linha do título. Cada
 * seção decide sozinha se tem as duas coisas, só uma delas, ou nenhuma — por
 * isso `descricao` é opcional, independente de `onNovo` vir ou não.
 */
import { type ReactNode } from 'react';

import { ErroDaApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { Skeleton } from '@/shared/ui/skeleton';
import { Vazio } from '@/shared/ui/vazio';

interface SecaoCadastroProps {
  titulo: string;
  /**
   * Frase curta da seção: embaixo do título quando o botão de acrescentar
   * aparece, à direita dele quando não aparece. Ausente na seção que não tem
   * essa frase para mostrar.
   */
  descricao?: string;
  /**
   * Rótulo do botão de acrescentar, como `+ Nova residência`.
   *
   * Some junto com `onNovo`, e por isso é opcional: a seção que só mostra o que
   * outra tela cadastra não tem botão, e não teria rótulo para dar a ele.
   */
  rotuloNovo?: string;
  /** Chamado ao clicar em acrescentar; ausente esconde o botão. */
  onNovo?: () => void;
  carregando: boolean;
  /** O erro da consulta, ou `null` quando ela respondeu. */
  erro: unknown;
  /** Chamado ao pedir nova tentativa depois de uma falha. */
  onTentarDeNovo: () => void;
  /** Verdadeiro quando a consulta respondeu sem nenhuma linha. */
  vazia: boolean;
  mensagemVazia: string;
  /** A tabela, desenhada apenas quando há linhas. */
  children: ReactNode;
}

/** Quantas linhas de esqueleto desenhar enquanto a consulta não responde. */
const LINHAS_DE_ESPERA = 3;

/**
 * Uma seção da tela, com cabeçalho e o estado em que a consulta está.
 *
 * @param titulo - O nome da seção, no topo.
 * @param descricao - Uma linha dizendo o que o cadastro determina, embaixo do
 *   título quando há botão de acrescentar e ao lado dele quando não há;
 *   ausente na seção que não tem essa frase para mostrar.
 * @param rotuloNovo - Texto do botão de acrescentar; sem ele o botão não é
 *   desenhado, ainda que `onNovo` venha.
 * @param onNovo - Chamado ao clicar em acrescentar; ausente esconde o botão.
 * @param carregando - Verdadeiro enquanto a primeira resposta não chegou.
 * @param erro - O que a consulta rejeitou, ou `null`.
 * @param onTentarDeNovo - Chamado para repetir a consulta que falhou.
 * @param vazia - Verdadeiro quando a resposta veio sem linha alguma.
 * @param mensagemVazia - O que dizer quando não há nada cadastrado.
 * @param children - A tabela desta seção.
 * @returns A seção com o estado que couber.
 */
export function SecaoCadastro({
  titulo,
  descricao,
  rotuloNovo,
  onNovo,
  carregando,
  erro,
  onTentarDeNovo,
  vazia,
  mensagemVazia,
  children,
}: SecaoCadastroProps) {
  return (
    <section className="mb-10">
      <CabecalhoDeSecao
        titulo={titulo}
        subtitulo={descricao}
        acoes={
          onNovo !== undefined && rotuloNovo !== undefined ? (
            <Button className="px-4 py-2 text-[13px]" onClick={onNovo}>
              {rotuloNovo}
            </Button>
          ) : undefined
        }
      />

      {carregando && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: LINHAS_DE_ESPERA }, (_, indice) => (
            <Skeleton key={indice} className="h-12 rounded-md" />
          ))}
        </div>
      )}

      {!carregando && erro != null && (
        <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
          <p className="font-serif text-lg text-foreground">Não foi possível carregar</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {erro instanceof ErroDaApi
              ? erro.message
              : 'A consulta falhou antes de chegar ao servidor.'}
          </p>
          <Button variant="outline" className="mt-4" onClick={onTentarDeNovo}>
            Tentar de novo
          </Button>
        </div>
      )}

      {!carregando && erro == null && vazia && (
        <Vazio
          mensagem={mensagemVazia}
          rotuloAcao={onNovo !== undefined ? rotuloNovo : undefined}
          onAcao={onNovo}
          varianteAcao="default"
        />
      )}

      {!carregando && erro == null && !vazia && children}
    </section>
  );
}
